<template>
  <div class="page">
    <!-- 顶部导航栏：返回按钮 + 标题 -->
    <div class="nav-bar">
      <div class="nav-back" @click="goBack">
        <!-- 返回箭头 -->
        <svg viewBox="0 0 24 24">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </div>
      <div class="nav-title">意见反馈</div>
    </div>

    <!-- 反馈列表：分页加载，点一次加载更多追加一页 -->
    <div class="feedback-list">
      <div v-for="row in rows" :key="row.id" class="feedback-item">
        <!-- 第一行：反馈状态标签（待回复=橙色，已回复=绿色） -->
        <div class="feedback-head">
          <span
            class="tag"
            :class="row.feedbackStatus === 'reply' ? 'tag-reply' : 'tag-pending'"
          >{{ statusText(row.feedbackStatus) }}</span>
        </div>

        <!-- 反馈内容 -->
        <div class="feedback-content">{{ row.feedbackContent }}</div>

        <!-- 官方回复：有回复内容才显示 -->
        <div v-if="row.feedbackReply" class="reply-box">
          <div class="reply-label">官方回复</div>
          <div class="reply-text">{{ row.feedbackReply }}</div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-if="!loading && rows.length === 0" class="empty">暂无反馈记录</div>

      <!-- 加载更多：取完提示没有更多 -->
      <div
        v-if="rows.length > 0"
        class="load-more"
        @click="loadMore"
      >
        {{ hasMore ? (loading ? '加载中...' : '加载更多') : '— 没有更多了 —' }}
      </div>
    </div>

    <!-- 新增反馈按钮：固定在页面底部，点击打开弹窗 -->
    <button class="add-btn" @click="openAdd">+ 写反馈</button>

    <!-- ===== 新增反馈弹窗 ===== -->
    <div v-if="showAdd" class="modal-mask" @click.self="closeAdd">
      <div class="modal-box">
        <div class="modal-head">
          <span class="modal-title">新增反馈</span>
          <span class="modal-close" @click="closeAdd">×</span>
        </div>

        <!-- 反馈内容输入框：多行文本，最多 200 字 -->
        <textarea
          v-model="feedbackContent"
          class="feedback-textarea"
          maxlength="200"
          placeholder="请输入您的问题或建议（200 字以内）"
        ></textarea>
        <!-- 字数统计 -->
        <div class="count">{{ feedbackContent.length }}/200</div>

        <!-- 提交按钮：内容为空或提交中时置灰不可点 -->
        <button
          class="submit-btn"
          :disabled="submitting || !feedbackContent.trim()"
          @click="handleSubmit"
        >
          {{ submitting ? '提交中...' : '提交反馈' }}
        </button>
      </div>
    </div>

    <!-- 顶部轻提示：提交成功后短暂显示 -->
    <div v-if="toast" class="toast">{{ toast }}</div>

    <!-- 请求失败提示 -->
    <div v-if="errorMsg" class="error-msg">{{ errorMsg }}</div>
  </div>
</template>

<script>
// 导入反馈相关接口：分页查询 + 新增反馈
import { getFeedbackPage, createFeedback } from '@/api/help'

export default {
  name: 'FeedbackView',
  data() {
    return {
      rows: [],     // 反馈列表数据（加载更多时往后追加）
      total: 0,     // 后端返回的总条数
      pageNum: 1,   // 当前页码
      pageSize: 10, // 每页条数
      loading: false,
      errorMsg: '',
      // 新增反馈弹窗
      showAdd: false,       // 弹窗是否显示
      feedbackContent: '',  // 反馈内容（双向绑定输入框）
      submitting: false,    // 是否提交中（防止重复点击）
      // 顶部轻提示文字，显示 2 秒后自动消失
      toast: ''
    }
  },
  computed: {
    // 已加载条数少于总数，说明还能继续加载
    hasMore() {
      return this.rows.length < this.total
    }
  },
  created() {
    this.fetchList()
  },
  methods: {
    // 反馈状态编码转中文：pending=待回复，reply=已回复；未知值原样显示
    statusText(value) {
      const map = { pending: '待回复', reply: '已回复' }
      return map[value] || value || ''
    },

    // 请求反馈列表
    async fetchList() {
      this.loading = true
      this.errorMsg = ''
      try {
        const res = await getFeedbackPage({
          pageNum: this.pageNum,
          pageSize: this.pageSize
        })
        // 后端返回格式：{ code: "200", data: { total, rows }, msg, success }
        if (res.code === '200' || res.success) {
          this.rows = this.rows.concat((res.data && res.data.rows) || [])
          this.total = (res.data && res.data.total) || 0
        } else {
          this.errorMsg = res.msg || '请求失败，请重试'
        }
      } catch (err) {
        this.errorMsg = '网络错误，请稍后重试'
        console.error(err)
      } finally {
        this.loading = false
      }
    },

    // 加载下一页，追加到列表
    loadMore() {
      if (!this.hasMore || this.loading) return
      this.pageNum += 1
      this.fetchList()
    },

    // 打开新增反馈弹窗：清空上次输入的内容
    openAdd() {
      this.feedbackContent = ''
      this.showAdd = true
    },

    // 关闭新增反馈弹窗
    closeAdd() {
      this.showAdd = false
    },

    // 提交反馈：调用新增接口，成功后关闭弹窗并回到第一页刷新列表
    async handleSubmit() {
      // 去掉首尾空格后再判断，防止提交纯空格内容
      const content = this.feedbackContent.trim()
      if (!content || this.submitting) return
      this.submitting = true
      try {
        const res = await createFeedback({ feedbackContent: content })
        // 后端返回格式：{ code: "200", data: true, msg, success }
        if (res.code === '200' || res.success) {
          this.closeAdd()
          this.showToast('提交成功，感谢您的反馈！')
          // 刷新列表：清空数据后从第一页重新查询
          this.rows = []
          this.total = 0
          this.pageNum = 1
          this.fetchList()
        } else {
          alert(res.msg || '提交失败，请重试')
        }
      } catch (err) {
        alert('网络错误，请稍后重试')
        console.error(err)
      } finally {
        this.submitting = false
      }
    },

    // 显示顶部轻提示，2 秒后自动消失
    showToast(text) {
      this.toast = text
      setTimeout(() => {
        this.toast = ''
      }, 2000)
    },

    // 返回主页
    goBack() {
      this.$router.push('/home')
    }
  }
}
</script>

<style scoped>
/* 页面整体：与其他子页面一致的手机门户风格 */
.page {
  width: 100%;
  max-width: 480px;
  min-height: 100%;
  margin: 0 auto;
  background: #f5f6fa;
}

/* ===== 顶部导航栏 ===== */
.nav-bar {
  position: relative;
  height: 48px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-back {
  position: absolute;
  left: 10px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.nav-back svg {
  width: 22px;
  height: 22px;
  fill: none;
  stroke: #fff;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.nav-title {
  color: #fff;
  font-size: 16px;
}

/* ===== 反馈列表 ===== */
.feedback-list {
  /* 底部留出新增按钮的高度，避免最后一条被盖住 */
  padding: 12px 12px 90px;
}

.feedback-item {
  background: #fff;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 12px;
}

.feedback-head {
  display: flex;
  justify-content: flex-end; /* 状态标签靠右 */
  margin-bottom: 8px;
}

/* 状态标签通用样式 */
.tag {
  font-size: 12px;
  padding: 2px 10px;
  border-radius: 10px;
}

/* 待回复：橙色 */
.tag-pending {
  color: #e6a23c;
  background: #fdf6ec;
  border: 1px solid #f5dab1;
}

/* 已回复：绿色 */
.tag-reply {
  color: #67c23a;
  background: #f0f9eb;
  border: 1px solid #c2e7b0;
}

/* 反馈内容 */
.feedback-content {
  font-size: 15px;
  color: #303133;
  line-height: 1.6;
  word-break: break-all; /* 长单词/链接强制换行 */
}

/* 官方回复块：浅紫背景，与正文区分开 */
.reply-box {
  margin-top: 10px;
  padding: 10px 12px;
  background: #f4f5fb;
  border-left: 3px solid #667eea;
  border-radius: 6px;
}

.reply-label {
  font-size: 13px;
  color: #667eea;
  font-weight: bold;
  margin-bottom: 4px;
}

.reply-text {
  font-size: 14px;
  color: #606266;
  line-height: 1.5;
  word-break: break-all;
}

/* 空状态 */
.empty {
  text-align: center;
  color: #909399;
  font-size: 14px;
  padding: 40px 0;
}

/* 加载更多 */
.load-more {
  text-align: center;
  color: #667eea;
  font-size: 14px;
  padding: 12px 0 4px;
  cursor: pointer;
}

/* ===== 新增反馈按钮：固定在页面底部 ===== */
.add-btn {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  /* 配合页面最大 480px：用 calc 限制按钮宽度，超出部分按 456px（480-24）算 */
  width: calc(min(100vw, 480px) - 24px);
  height: 46px;
  border: none;
  border-radius: 23px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  letter-spacing: 2px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.add-btn:active {
  opacity: 0.85; /* 按下去的反馈 */
}

/* ===== 新增反馈弹窗 ===== */
.modal-mask {
  position: fixed;
  inset: 0; /* 上下左右撑满 */
  background: rgba(0, 0, 0, 0.5);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-box {
  width: calc(min(100vw, 480px) - 60px);
  background: #fff;
  border-radius: 12px;
  padding: 16px;
}

.modal-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.modal-title {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}

.modal-close {
  font-size: 22px;
  color: #909399;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
}

/* 反馈内容输入框：手机端 16px 字号，防止 iPhone 聚焦时页面放大 */
.feedback-textarea {
  width: 100%;
  height: 120px;
  box-sizing: border-box;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 16px;
  color: #303133;
  resize: none; /* 禁止拖拽调整大小 */
  outline: none;
  font-family: inherit;
}

.feedback-textarea:focus {
  border-color: #667eea;
}

/* 字数统计 */
.count {
  text-align: right;
  font-size: 12px;
  color: #909399;
  margin: 6px 0 12px;
}

/* 提交按钮 */
.submit-btn {
  width: 100%;
  height: 42px;
  border: none;
  border-radius: 21px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  font-size: 15px;
  font-weight: bold;
  cursor: pointer;
}

.submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ===== 顶部轻提示 ===== */
.toast {
  position: fixed;
  top: 70px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  font-size: 14px;
  padding: 8px 18px;
  border-radius: 18px;
  z-index: 200;
  white-space: nowrap;
}

/* ===== 请求失败提示 ===== */
.error-msg {
  text-align: center;
  color: #f56c6c;
  font-size: 13px;
  padding: 0 12px 12px;
}
</style>
